import { supabase } from "@/lib/supabase";
import { trackOwnerFleetReminderUpdate } from "@/lib/fleet-reminder/track-fleet-reminder";
import { normalizeVehicle, normalizeVehicles } from "@/lib/utils/data-mapper";
import { validateCoordinates } from "@/lib/utils/coordinates";
import { handleError, ErrorContext } from "@/lib/utils/error-handler";
import { locationSearch, tripDurationDays } from "@/lib/search/search-availability";

export interface VehicleFilters {
  make?: string;
  model?: string;
  min_price?: number;
  max_price?: number;
  location?: string;
  status?: "available" | "unavailable" | "maintenance";
  seats?: number;
  transmission?: string;
  fuel_type?: string;
  color?: string | string[];
  features?: string[];
  category?: string;
  cancellation_policy?: "flexible" | "moderate" | "strict";
  fuel_policy?: string;
  /** When true, only return vehicles with no deposit (deposit null or 0) */
  no_deposit?: boolean;
  /** yyyy-MM-dd. Sans les deux dates, le minimum de jours n'est pas appliqué. */
  start_date?: string;
  end_date?: string;
  pickup_date?: string;
  dropoff_date?: string;
  /** Filter vehicles by owner (agency) ID */
  owner_id?: string;
  limit?: number;
  offset?: number;
}

export const vehicleService = {
  /**
   * Search vehicles with filters
   */
  async searchVehicles(filters: VehicleFilters = {}) {
    // Keep list query lean under Disk IO pressure — avoid select('*') + full owner row
    let query = supabase.from("vehicles").select(`
      id,
      make,
      model,
      year,
      price_per_day,
      location,
      images,
      status,
      publication_status,
      seats,
      transmission,
      fuel_type,
      category,
      features,
      color,
      deposit,
      is_test,
      owner_id,
      cancellation_policy,
      fuel_policy,
      instant_booking,
      created_at,
      minimum_rental_days,
      long_term_discounts,
      pickup_locations,
      availability_confirmed_at,
      owner:profiles!owner_id(
        id,
        first_name,
        last_name,
        agency_name,
        avatar_url,
        verified_host,
        role
      )
    `);

    if (filters.make) {
      query = query.ilike("make", `%${filters.make}%`);
    }

    if (filters.model) {
      query = query.ilike("model", `%${filters.model}%`);
    }

    if (filters.min_price !== undefined) {
      query = query.gte("price_per_day", filters.min_price);
    }

    if (filters.max_price !== undefined) {
      query = query.lte("price_per_day", filters.max_price);
    }

    if (filters.location) {
      const locationMatch = locationSearch(filters.location);
      if (locationMatch?.kind === "or") {
        query = query.or(locationMatch.filter);
      } else if (locationMatch?.kind === "ilike") {
        query = query.ilike("location", locationMatch.pattern);
      }
    }

    const requestedDays = tripDurationDays(
      filters.start_date ?? filters.pickup_date,
      filters.end_date ?? filters.dropoff_date
    );
    if (requestedDays != null) {
      // null en base = défaut produit 3 jours. On les garde seulement si le séjour les couvre.
      query = requestedDays >= 3
        ? query.or(`minimum_rental_days.is.null,minimum_rental_days.lte.${requestedDays}`)
        : query.lte("minimum_rental_days", requestedDays);
    }

    if (filters.owner_id) {
      query = query.eq("owner_id", filters.owner_id);
    }

    // Appliquer le filtre status si fourni, sinon "available" par défaut
    const statusFilter = filters.status || "available";
    query = query.eq("status", statusFilter);

    // TOUJOURS exclure les drafts de la recherche publique
    // Seuls les véhicules avec publication_status = 'active' doivent apparaître
    query = query.eq("publication_status", "active");

    // CRITICAL SAFETY: Always exclude test vehicles from public search
    // Test vehicles should never appear to real users
    query = query.or("is_test.is.null,is_test.eq.false");

    if (filters.seats) {
      query = query.eq("seats", filters.seats);
    }

    if (filters.transmission) {
      query = query.eq("transmission", filters.transmission);
    }

    if (filters.fuel_type) {
      query = query.eq("fuel_type", filters.fuel_type);
    }

    // Filtre par catégorie
    if (filters.category) {
      query = query.eq("category", filters.category);
    }

    // Filtre par couleur (support multi-sélection)
    if (filters.color) {
      if (Array.isArray(filters.color)) {
        // Multi-sélection : utiliser .in() pour plusieurs couleurs
        if (filters.color.length > 0) {
          query = query.in("color", filters.color);
        }
      } else {
        // Sélection unique
        query = query.eq("color", filters.color);
      }
    }

    if (filters.cancellation_policy) {
      query = query.eq("cancellation_policy", filters.cancellation_policy);
    }

    if (filters.fuel_policy) {
      query = query.eq("fuel_policy", filters.fuel_policy);
    }

    // Filtre "sans caution" : véhicules sans dépôt (deposit null ou 0)
    if (filters.no_deposit === true) {
      query = query.or("deposit.is.null,deposit.eq.0");
    }

    query = query.order("created_at", { ascending: false });

    // Default bound to avoid unbounded catalog pulls (callers can raise via filters.limit)
    const limit = filters.limit ?? 50;
    query = query.limit(limit);

    if (filters.offset !== undefined) {
      query = query.range(filters.offset, filters.offset + limit - 1);
    }

    const { data, error } = await query;

    if (error) {
      const context: ErrorContext = {
        action: "searchVehicles",
        component: "vehicleService",
        filters: filters,
      };
      handleError(error, context, { showToast: false, silent: false });
      throw new Error(error.message);
    }

    let vehicles = normalizeVehicles(data || []);

    // CRITICAL SAFETY: Client-side filter to exclude test vehicles (backup safety)
    // This ensures test vehicles are never shown even if query filter fails
    vehicles = vehicles.filter((vehicle) => vehicle.is_test !== true);

    // TOUJOURS exclure les véhicules non vérifiés
    // Récupérer les IDs des véhicules avec une demande de vérification approuvée
    const { data: verifiedVehicles, error: verificationError } = await supabase
      .from("vehicle_verification_requests")
      .select("vehicle_id")
      .eq("status", "approved");

    if (verificationError) {
      // Fail-open: do not wipe the catalog when verification lookup times out under load
      console.error("[vehicle.service] verified vehicles lookup failed:", verificationError.message);
      return vehicles;
    }

    const verifiedVehicleIds = new Set(verifiedVehicles?.map((v) => v.vehicle_id) || []);

    if (import.meta.env.DEV) {
      console.debug("[vehicle.service] Vehicle filtering stats:", {
        totalVehiclesBeforeFilter: vehicles.length,
        verifiedVehicleCount: verifiedVehicleIds.size,
        verifiedVehicleIds: Array.from(verifiedVehicleIds),
      });
    }

    // Filtrer pour ne garder que les véhicules vérifiés
    const vehiclesBeforeVerificationFilter = vehicles.length;
    vehicles = vehicles.filter((vehicle) => verifiedVehicleIds.has(vehicle.id));
    
    if (import.meta.env.DEV) {
      console.debug("[vehicle.service] Verification filter results:", {
        vehiclesBeforeFilter: vehiclesBeforeVerificationFilter,
        vehiclesAfterFilter: vehicles.length,
        filteredOut: vehiclesBeforeVerificationFilter - vehicles.length,
      });
    }

    // Filtre par équipements (client-side car Supabase ne supporte pas bien les arrays)
    if (filters.features && Array.isArray(filters.features) && filters.features.length > 0) {
      const vehiclesBeforeFeaturesFilter = vehicles.length;
      vehicles = vehicles.filter((vehicle) => {
        // S'assurer que features est un array valide
        const vehicleFeatures = vehicle?.features;
        if (!vehicleFeatures || !Array.isArray(vehicleFeatures) || vehicleFeatures.length === 0) {
          return false;
        }
        // Vérifier si le véhicule a au moins un des équipements sélectionnés
        return filters.features!.some((feature) => {
          return Array.isArray(vehicleFeatures) && vehicleFeatures.includes(feature);
        });
      });
      
      if (import.meta.env.DEV) {
        console.debug("[vehicle.service] Features filter results:", {
          vehiclesBeforeFilter: vehiclesBeforeFeaturesFilter,
          vehiclesAfterFilter: vehicles.length,
          filteredOut: vehiclesBeforeFeaturesFilter - vehicles.length,
        });
      }
    }

    if (import.meta.env.DEV) {
      console.debug("[vehicle.service] Final vehicle list length:", vehicles.length);
    }

    return vehicles;
  },

  /**
   * Get all public vehicles for a given owner/agency.
   * Returns only verified, active, non-test vehicles.
   */
  async getAgencyVehicles(ownerId: string) {
    return this.searchVehicles({ owner_id: ownerId });
  },

  /**
   * Get recommended vehicles: same city, similar price range (±30%), excluding a given vehicle.
   * Returns verified, active, non-test vehicles only.
   */
  async getRecommendedVehicles(vehicleId: string, location: string, pricePerDay: number, limit = 8) {
    const priceLow = Math.max(0, Math.round(pricePerDay * 0.7));
    const priceHigh = Math.round(pricePerDay * 1.3);

    let query = supabase
      .from("vehicles")
      .select("*, owner:profiles!owner_id(*)")
      .neq("id", vehicleId)
      .ilike("location", `%${location.trim()}%`)
      .gte("price_per_day", priceLow)
      .lte("price_per_day", priceHigh)
      .eq("status", "available")
      .eq("publication_status", "active")
      .or("is_test.is.null,is_test.eq.false")
      .order("rating", { ascending: false, nullsFirst: false })
      .limit(limit);

    const { data, error } = await query;

    if (error) {
      const context: ErrorContext = {
        action: "getRecommendedVehicles",
        component: "vehicleService",
        vehicleId,
      };
      handleError(error, context, { showToast: false, silent: true });
      return [];
    }

    let vehicles = normalizeVehicles(data || []);
    vehicles = vehicles.filter((v) => v.is_test !== true);

    const { data: verifiedVehicles } = await supabase
      .from("vehicle_verification_requests")
      .select("vehicle_id")
      .eq("status", "approved");

    const verifiedIds = new Set(verifiedVehicles?.map((v) => v.vehicle_id) || []);
    vehicles = vehicles.filter((v) => verifiedIds.has(v.id));

    return vehicles;
  },

  /**
   * Get vehicle by ID
   * Returns null if the vehicle doesn't exist instead of throwing an error
   * For non-active vehicles (draft, pending_review, rejected), only the owner can access
   */
  async getVehicleById(id: string) {
    const { data, error } = await supabase
      .from("vehicles")
      .select("*, owner:profiles!owner_id(*)")
      .eq("id", id)
      .single();

    if (error) {
      // Si le véhicule n'existe pas (PGRST116) ou erreur 406, retourner null
      if (
        error.code === "PGRST116" ||
        error.code === "PGRST301" ||
        error.message?.includes("Cannot coerce")
      ) {
        return null;
      }

      // Pour les autres erreurs, logger mais ne pas lancer d'erreur si c'est juste "not found"
      const context: ErrorContext = {
        action: "getVehicleById",
        component: "vehicleService",
        vehicleId: id,
      };
      handleError(error, context, { showToast: false, silent: true });

      // Si c'est une erreur autre que "not found", lancer l'erreur
      throw new Error(error.message);
    }

    // Vérifier les permissions pour les véhicules non-actifs
    const publicStatuses = ["active", "paused"];
    if (!publicStatuses.includes(data.publication_status)) {
      // Le véhicule n'est pas public, vérifier si l'utilisateur est le propriétaire
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        // Utilisateur non connecté, pas d'accès aux drafts/pending
        return null;
      }

      if (data.owner_id !== user.id) {
        // Vérifier si l'utilisateur est admin
        const { data: profile } = await supabase
          .from("profiles")
          .select("role")
          .eq("id", user.id)
          .single();

        if (!profile || profile.role !== "admin") {
          // Pas le propriétaire ni admin, pas d'accès
          return null;
        }
      }
    }

    return normalizeVehicle(data);
  },

  /**
   * Get my vehicles (owner)
   */
  async getMyVehicles() {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const { data, error } = await supabase
      .from("vehicles")
      .select("*")
      .eq("owner_id", user.id)
      .order("created_at", { ascending: false });

    if (error) {
      const context: ErrorContext = {
        action: "getMyVehicles",
        component: "vehicleService",
        userId: user.id,
      };
      handleError(error, context, { showToast: false, silent: false });
      throw new Error(error.message);
    }
    return normalizeVehicles(data || []);
  },

  /**
   * Check vehicle availability
   * CORRECTION: Inclure 'in_progress' dans les statuts bloquants
   */
  async checkAvailability(vehicleId: string, startDate: string, endDate: string) {
    const { BLOCKING_BOOKING_STATUSES } = await import("@/lib/types/booking-status");
    const { data: bookings, error } = await supabase
      .from("bookings")
      .select("id, start_date, end_date, status")
      .eq("vehicle_id", vehicleId)
      .in("status", [...BLOCKING_BOOKING_STATUSES] as any[]);

    if (error) {
      const context: ErrorContext = {
        action: "checkAvailability",
        component: "vehicleService",
        vehicleId: vehicleId,
      };
      handleError(error, context, { showToast: false, silent: false });
      throw new Error(error.message);
    }

    const hasConflict = bookings?.some((booking) => {
      const bookingStart = new Date(booking.start_date);
      const bookingEnd = new Date(booking.end_date);
      const requestedStart = new Date(startDate);
      const requestedEnd = new Date(endDate);

      return (
        (requestedStart >= bookingStart && requestedStart <= bookingEnd) ||
        (requestedEnd >= bookingStart && requestedEnd <= bookingEnd) ||
        (requestedStart <= bookingStart && requestedEnd >= bookingEnd)
      );
    });

    return {
      available: !hasConflict,
      conflictingBookings: bookings || [],
    };
  },

  /**
   * Create vehicle
   * @param data - Vehicle data
   * @param options - Optional parameters for admin mode
   * @param options.ownerId - Owner ID (for admin mode, when creating vehicle for an agency)
   */
  async createVehicle(
    data: Record<string, unknown>,
    options?: { ownerId?: string }
  ) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    // Validate coordinates if provided
    if (data.latitude !== undefined || data.longitude !== undefined) {
      const latitude = data.latitude as number | null | undefined;
      const longitude = data.longitude as number | null | undefined;

      if (!validateCoordinates(latitude, longitude)) {
        throw new Error(
          "Coordonnées invalides. La latitude doit être entre -90 et 90, et la longitude entre -180 et 180."
        );
      }
    }

    // Déterminer le owner_id et si c'est un admin qui crée
    let ownerId: string;
    let createdByAdmin = false;
    let adminUserId: string | null = null;

    if (options?.ownerId) {
      // Mode admin : créer pour une agence spécifiée
      // Vérifier que l'utilisateur est admin
      const { data: profile } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", user.id)
        .single();

      if (!profile || profile.role !== "admin") {
        throw new Error("Unauthorized: Only admins can create vehicles for other agencies");
      }

      // Vérifier que l'owner_id spécifié est bien une agence (role = owner)
      const { data: ownerProfile } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", options.ownerId)
        .single();

      if (!ownerProfile || ownerProfile.role !== "owner") {
        throw new Error("Invalid owner: The specified owner must be an agency (role = owner)");
      }

      ownerId = options.ownerId;
      createdByAdmin = true;
      adminUserId = user.id;
    } else {
      // Mode normal : owner crée son propre véhicule
      ownerId = user.id;
    }

    // Filtrer les propriétés undefined pour éviter les erreurs de schéma
    const insertData: Record<string, unknown> = {
      owner_id: ownerId,
      images: data.images || [],
      features: data.features || [],
      status: data.status || "unavailable", // Unavailable jusqu'à approbation
      // Permettre de spécifier publication_status (draft, pending_review, etc.)
      publication_status: data.publication_status || "pending_review", // Par défaut: soumis à vérification
      created_by_admin: createdByAdmin,
      admin_user_id: adminUserId,
    };

    // Ajouter toutes les autres propriétés qui ne sont pas undefined
    // Exclure cancellation_policy car la politique RAKB unique est appliquée automatiquement
    Object.entries(data).forEach(([key, value]) => {
      if (
        value !== undefined &&
        ![
          "images",
          "features",
          "status",
          "publication_status",
          "cancellation_policy",
          "owner_id", // Exclure owner_id car on le gère explicitement
        ].includes(key)
      ) {
        insertData[key] = value;
      }
    });

    const { data: vehicle, error } = await supabase
      .from("vehicles")
      .insert(insertData)
      .select()
      .single();

    if (error) {
      const context: ErrorContext = {
        action: "createVehicle",
        component: "vehicleService",
        userId: user.id,
      };
      handleError(error, context, { showToast: false, silent: false });
      throw new Error(error.message);
    }
    return normalizeVehicle(vehicle);
  },

  /**
   * Update vehicle
   */
  async updateVehicle(id: string, data: Record<string, unknown>) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    // Verify ownership
    const { data: vehicle } = await supabase
      .from("vehicles")
      .select("owner_id")
      .eq("id", id)
      .single();

    if (!vehicle || vehicle.owner_id !== user.id) {
      throw new Error("Unauthorized: Not the owner");
    }

    // Validate coordinates if provided
    if (data.latitude !== undefined || data.longitude !== undefined) {
      const latitude = data.latitude as number | null | undefined;
      const longitude = data.longitude as number | null | undefined;

      if (!validateCoordinates(latitude, longitude)) {
        throw new Error(
          "Coordonnées invalides. La latitude doit être entre -90 et 90, et la longitude entre -180 et 180."
        );
      }
    }

    // Exclure cancellation_policy car la politique RAKB unique est appliquée automatiquement
    const { cancellation_policy, ...updateData } = data;

    const { data: updated, error } = await supabase
      .from("vehicles")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      const context: ErrorContext = {
        action: "updateVehicle",
        component: "vehicleService",
        vehicleId: id,
      };
      handleError(error, context, { showToast: false, silent: false });
      throw new Error(error.message);
    }

    const tracksFleetChange = ["price_per_day", "price", "status", "publication_status"].some(
      (field) => field in data
    );
    if (tracksFleetChange) {
      void trackOwnerFleetReminderUpdate(user.id);
    }
    return normalizeVehicle(updated);
  },

  /**
   * Delete vehicle
   */
  async deleteVehicle(id: string) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    // Verify ownership
    const { data: vehicle } = await supabase
      .from("vehicles")
      .select("owner_id, images")
      .eq("id", id)
      .single();

    if (!vehicle || vehicle.owner_id !== user.id) {
      throw new Error("Unauthorized: Not the owner");
    }

    const { BLOCKING_BOOKING_STATUSES } = await import("@/lib/types/booking-status");
    const { data: activeBookings, error: bookingsError } = await supabase
      .from("bookings")
      .select("id, status, start_date, end_date")
      .eq("vehicle_id", id)
      .in("status", [...BLOCKING_BOOKING_STATUSES] as any[]);

    if (bookingsError) {
      throw new Error(`Erreur lors de la vérification des réservations: ${bookingsError.message}`);
    }

    if (activeBookings && activeBookings.length > 0) {
      const bookingCount = activeBookings.length;
      throw new Error(
        `Impossible de supprimer ce véhicule : ${bookingCount} réservation${bookingCount > 1 ? "s" : ""} active${bookingCount > 1 ? "s" : ""} en cours. Veuillez annuler ou compléter les réservations avant de supprimer le véhicule.`
      );
    }

    // Delete images from storage
    if (vehicle.images && Array.isArray(vehicle.images)) {
      const imagePaths = vehicle.images
        .map((url: string) => {
          const parts = url.split("/");
          const index = parts.findIndex((p: string) => p === "vehicles");
          if (index !== -1) {
            return parts.slice(index + 1).join("/");
          }
          return null;
        })
        .filter((p: string | null): p is string => p !== null);

      if (imagePaths.length > 0) {
        await supabase.storage.from("vehicles").remove(imagePaths);
      }
    }

    const { error } = await supabase.from("vehicles").delete().eq("id", id);
    if (error) {
      const context: ErrorContext = {
        action: "deleteVehicle",
        component: "vehicleService",
        vehicleId: id,
        userId: user.id,
      };
      handleError(error, context, { showToast: false, silent: false });
      throw new Error(error.message);
    }
  },

  /**
   * Submit a vehicle update request for admin approval (owner only).
   * The update is applied only after an admin approves it.
   */
  async submitVehicleUpdateRequest(
    vehicleId: string,
    vehiclePayload: Record<string, unknown>,
    extras: Array<{ name: string; description?: string; price_per_day: number; is_available?: boolean }>
  ): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    const { data: vehicle } = await supabase
      .from("vehicles")
      .select("owner_id")
      .eq("id", vehicleId)
      .single();

    if (!vehicle || vehicle.owner_id !== user.id) {
      throw new Error("Unauthorized: Not the owner");
    }

    const payload = {
      vehicle: vehiclePayload,
      extras: extras.map((e) => ({
        name: e.name.trim(),
        price_per_day: e.price_per_day,
        description: e.description?.trim() ?? undefined,
      })),
    };

    const { error } = await supabase.from("vehicle_update_requests").insert({
      vehicle_id: vehicleId,
      requested_by: user.id,
      payload,
      status: "pending",
    });

    if (error) {
      throw new Error(error.message || "Échec envoi de la demande de mise à jour");
    }
  },

  /**
   * List pending vehicle update requests (admin only; RLS enforces role).
   */
  async getPendingVehicleUpdateRequests(): Promise<
    Array<{
      id: string;
      vehicle_id: string;
      requested_by: string;
      payload: { vehicle?: Record<string, unknown>; extras?: Array<{ name: string; price_per_day: number; description?: string }> };
      status: string;
      created_at: string;
      updated_at: string;
    }>
  > {
    const { data, error } = await supabase
      .from("vehicle_update_requests")
      .select("id, vehicle_id, requested_by, payload, status, created_at, updated_at")
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    if (error) {
      throw new Error(error.message || "Échec chargement des demandes");
    }

    return (data || []) as Array<{
      id: string;
      vehicle_id: string;
      requested_by: string;
      payload: { vehicle?: Record<string, unknown>; extras?: Array<{ name: string; price_per_day: number; description?: string }> };
      status: string;
      created_at: string;
      updated_at: string;
    }>;
  },

  /**
   * Approve a vehicle update request and apply changes (admin only; RPC enforces role).
   */
  async approveVehicleUpdateRequest(requestId: string): Promise<void> {
    const { error } = await supabase.rpc("admin_apply_vehicle_update_request", {
      p_request_id: requestId,
    });

    if (error) {
      throw new Error(error.message || "Échec approbation");
    }
  },

  /**
   * Reject a vehicle update request (admin only; RPC enforces role).
   */
  async rejectVehicleUpdateRequest(requestId: string): Promise<void> {
    const { error } = await supabase.rpc("admin_reject_vehicle_update_request", {
      p_request_id: requestId,
    });

    if (error) {
      throw new Error(error.message || "Échec rejet");
    }
  },

  /**
   * Upload vehicle image
   */
  async uploadImage(vehicleId: string, file: File) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    // Verify ownership
    const vehicle = await this.getVehicleById(vehicleId);
    if (vehicle.owner_id !== user.id) {
      throw new Error("Unauthorized: Not the owner");
    }

    const fileExt = file.name.split(".").pop();
    const fileName = `${vehicleId}/${Date.now()}.${fileExt}`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from("vehicles")
      .upload(fileName, file, {
        upsert: false,
      });

    if (uploadError) throw new Error(uploadError.message);

    const { data: urlData } = supabase.storage.from("vehicles").getPublicUrl(uploadData.path);

    // Update vehicle images array
    const currentImages = (vehicle.images as string[]) || [];
    const updatedImages = [...currentImages, urlData.publicUrl];

    return await this.updateVehicle(vehicleId, { images: updatedImages });
  },

  /**
   * Get vehicle statistics
   */
  async getVehicleStats(vehicleId: string) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    // Verify ownership
    const vehicle = await this.getVehicleById(vehicleId);
    if (vehicle.owner_id !== user.id) {
      throw new Error("Unauthorized: Not the owner");
    }

    // Get bookings count
    const { count: bookingsCount } = await supabase
      .from("bookings")
      .select("*", { count: "exact", head: true })
      .eq("vehicle_id", vehicleId)
      .in("status", ["confirmed", "completed"]);

    // Get total revenue
    const { data: bookings } = await supabase
      .from("bookings")
      .select("total_price")
      .eq("vehicle_id", vehicleId)
      .in("status", ["confirmed", "completed"]);

    const totalRevenue = bookings?.reduce((sum, b) => sum + Number(b.total_price || 0), 0) || 0;

    // Get average rating
    const { data: reviews } = await supabase
      .from("reviews")
      .select("rating")
      .eq("vehicle_id", vehicleId);

    const avgRating =
      reviews && reviews.length > 0
        ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
        : 0;

    return {
      bookingsCount: bookingsCount || 0,
      totalRevenue,
      averageRating: avgRating,
      reviewsCount: reviews?.length || 0,
    };
  },

  /**
   * Récupère les options extra d'un véhicule
   */
  async getVehicleExtras(vehicleId: string) {
    const { data, error } = await supabase
      .from("vehicle_extras")
      .select("*")
      .eq("vehicle_id", vehicleId)
      .eq("is_available", true)
      .order("created_at", { ascending: true });

    if (error) {
      const context: ErrorContext = {
        action: "getVehicleExtras",
        component: "vehicleService",
        vehicleId,
      };
      handleError(error, context, { showToast: false, silent: false });
      throw new Error(error.message);
    }

    return data || [];
  },

  /**
   * Crée des options extra pour un véhicule
   */
  async createVehicleExtras(
    vehicleId: string,
    extras: Array<{
      name: string;
      description?: string;
      price_per_day: number;
      is_available?: boolean;
    }>
  ) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    // Vérifier que l'utilisateur est propriétaire du véhicule
    const { data: vehicle } = await supabase
      .from("vehicles")
      .select("owner_id")
      .eq("id", vehicleId)
      .single();

    if (!vehicle || vehicle.owner_id !== user.id) {
      throw new Error("Unauthorized: Not the owner");
    }

    // Préparer les données pour insertion
    const extrasToInsert = extras.map((extra) => ({
      vehicle_id: vehicleId,
      name: extra.name.trim(),
      description: extra.description?.trim() || null,
      price_per_day: extra.price_per_day,
      is_available: extra.is_available !== undefined ? extra.is_available : true,
    }));

    const { data, error } = await supabase
      .from("vehicle_extras")
      .insert(extrasToInsert)
      .select();

    if (error) {
      const context: ErrorContext = {
        action: "createVehicleExtras",
        component: "vehicleService",
        vehicleId,
      };
      handleError(error, context, { showToast: false, silent: false });
      throw new Error(error.message);
    }

    return data || [];
  },

  /**
   * Met à jour une option extra
   */
  async updateVehicleExtra(
    extraId: string,
    updates: {
      name?: string;
      description?: string;
      price_per_day?: number;
      is_available?: boolean;
    }
  ) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    // Vérifier que l'utilisateur est propriétaire du véhicule associé
    const { data: extra } = await supabase
      .from("vehicle_extras")
      .select("vehicle_id, vehicles!inner(owner_id)")
      .eq("id", extraId)
      .single();

    if (!extra || (extra.vehicles as any)?.owner_id !== user.id) {
      throw new Error("Unauthorized: Not the owner");
    }

    const updateData: Record<string, unknown> = {};
    if (updates.name !== undefined) updateData.name = updates.name.trim();
    if (updates.description !== undefined)
      updateData.description = updates.description?.trim() || null;
    if (updates.price_per_day !== undefined) updateData.price_per_day = updates.price_per_day;
    if (updates.is_available !== undefined) updateData.is_available = updates.is_available;

    const { data, error } = await supabase
      .from("vehicle_extras")
      .update(updateData)
      .eq("id", extraId)
      .select()
      .single();

    if (error) {
      const context: ErrorContext = {
        action: "updateVehicleExtra",
        component: "vehicleService",
      };
      handleError(error, context, { showToast: false, silent: false });
      throw new Error(error.message);
    }

    return data;
  },

  /**
   * Supprime une option extra
   */
  async deleteVehicleExtra(extraId: string) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    // Vérifier que l'utilisateur est propriétaire du véhicule associé
    const { data: extra } = await supabase
      .from("vehicle_extras")
      .select("vehicle_id, vehicles!inner(owner_id)")
      .eq("id", extraId)
      .single();

    if (!extra || (extra.vehicles as any)?.owner_id !== user.id) {
      throw new Error("Unauthorized: Not the owner");
    }

    const { error } = await supabase.from("vehicle_extras").delete().eq("id", extraId);

    if (error) {
      const context: ErrorContext = {
        action: "deleteVehicleExtra",
        component: "vehicleService",
      };
      handleError(error, context, { showToast: false, silent: false });
      throw new Error(error.message);
    }
  },

  /**
   * Get all available cities from vehicles in the database
   * Returns unique cities sorted alphabetically
   * Only includes cities that have at least one verified and available vehicle
   */
  async getAvailableCities(): Promise<string[]> {
    try {
      // Utiliser la même logique que searchVehicles pour garantir la cohérence
      // Récupérer d'abord tous les véhicules disponibles et actifs
      const query = supabase
        .from("vehicles")
        .select("location, id")
        .eq("status", "available")
        .eq("publication_status", "active")
        .or("is_test.is.null,is_test.eq.false")
        .not("location", "is", null);

      const { data: vehiclesData, error: vehiclesError } = await query;

      if (vehiclesError) {
        throw vehiclesError;
      }

      if (!vehiclesData || vehiclesData.length === 0) {
        if (import.meta.env.DEV) {
          console.debug("[vehicle.service] No available vehicles found, returning default cities");
        }
        return [
          "Casablanca",
          "Rabat",
          "Marrakech",
          "Fès",
          "Tanger",
          "Agadir",
        ];
      }

      // Récupérer les IDs des véhicules vérifiés (même logique que searchVehicles)
      const { data: verifiedVehicles, error: verificationError } = await supabase
        .from("vehicle_verification_requests")
        .select("vehicle_id")
        .eq("status", "approved");

      if (verificationError && import.meta.env.DEV) {
        console.debug("[vehicle.service] Error fetching verified vehicles for cities:", verificationError);
      }

      const verifiedVehicleIds = new Set(verifiedVehicles?.map((v) => v.vehicle_id) || []);

      // Filtrer pour ne garder que les véhicules vérifiés
      const verifiedVehiclesData = vehiclesData.filter((v) => verifiedVehicleIds.has(v.id));

      if (error) {
        const context: ErrorContext = {
          action: "getAvailableCities",
          component: "vehicleService",
        };
        handleError(error, context, { showToast: false, silent: true });
        // En cas d'erreur, retourner la liste statique par défaut
        return [
          "Casablanca",
          "Rabat",
          "Marrakech",
          "Fès",
          "Tanger",
          "Agadir",
          "Meknès",
          "Oujda",
          "Kénitra",
          "Tétouan",
          "Safi",
          "Mohammedia",
          "El Jadida",
          "Béni Mellal",
          "Nador",
          "Taza",
          "Khémisset",
          "Larache",
          "Khouribga",
          "Settat",
          "Essaouira",
          "Ouarzazate",
          "Chefchaouen",
          "Ifrane",
          "Errachidia"
        ];
      }

      // Extraire les villes uniques, normaliser et trier
      const cities = new Set<string>();
      if (verifiedVehiclesData) {
        verifiedVehiclesData.forEach((vehicle) => {
          if (vehicle.location && vehicle.location.trim()) {
            cities.add(vehicle.location.trim());
          }
        });
      }

      // Convertir en array et trier alphabétiquement
      const sortedCities = Array.from(cities).sort((a, b) =>
        a.localeCompare(b, "fr", { sensitivity: "base" })
      );

      if (import.meta.env.DEV) {
        console.debug("[vehicle.service] Available cities found:", {
          totalCities: sortedCities.length,
          cities: sortedCities.slice(0, 10), // Log first 10
        });
      }

      // Si aucune ville trouvée, retourner la liste par défaut
      if (sortedCities.length === 0) {
        return [
          "Casablanca",
          "Rabat",
          "Marrakech",
          "Fès",
          "Tanger",
          "Agadir",
        ];
      }

      return sortedCities;
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("[vehicle.service] Error fetching available cities:", error);
      }
      // En cas d'erreur, retourner la liste statique par défaut
      return [
        "Casablanca",
        "Rabat",
        "Marrakech",
        "Fès",
        "Tanger",
        "Agadir",
      ];
    }
  },
};
